import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { GlobalStoreContextType } from "../context/GlobalStoreContext";
import { CalendarEvent } from "./CalendarEvent";
import { EquipmentModel } from "./Equipment.model";
import { MapObject } from "./MapObject";
import { RepeatingCalendarEventDefinition } from "./RepeatingCalendarEventDefinition";

dayjs.extend(isBetween);

export class Equipment extends EquipmentModel {
    static fromFeature(feature: GeoJSON.Feature, project: any, timezone: string): Equipment {
        const def = project.repeating_calendar_event_definitions?.find(
            (def: any) => def.template_calendar_event?.name === "Project Hours"
        );

        const parsed = def ? RepeatingCalendarEventDefinition.fromApiObject(def, timezone) : null;

        const equipment = Equipment.fromApiObject(
            {
                id: null,
                project_id: project.id,
                match_jobsite_hours: true,
                repeating_calendar_event_definitions: [
                    {
                        rrule: def?.rrule || "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR",
                        project_id: project.id,
                        template_calendar_event: {
                            project_id: project.id,
                            calendar_event_type: "availability",
                            name: "Hours",
                            start_at:
                                parsed?.templateCalendarEvent?.startAt ||
                                dayjs().tz(timezone).startOf("day").add(6, "hour"),
                            end_at:
                                parsed?.templateCalendarEvent?.endAt ||
                                dayjs().tz(timezone).startOf("day").add(18, "hour"),
                            is_template: true,
                        },
                    },
                ],
            },
            timezone
        );

        return equipment;
    }

    getMapObject(globalStoreContext: GlobalStoreContextType): MapObject | null {
        const projectMapObjects = globalStoreContext.mapObjectsForProject(this.projectId || "");
        const mapObject = projectMapObjects.find(
            (mapObject) => mapObject.mappableType == "Equipment" && mapObject.mappableId == this.id
        );

        if (mapObject) {
            return mapObject;
        }

        return null;
    }

    openHoursDefinition(): RepeatingCalendarEventDefinition | null {
        const defs: RepeatingCalendarEventDefinition[] = (this.repeatingCalendarEventDefinitions || []).map(
            (def: any) =>
                RepeatingCalendarEventDefinition.fromApiObject(def, this.project?.timezone || dayjs.tz.guess())
        );

        const hours = defs.find((def) => def.templateCalendarEvent?.calendarEventType === "availability");

        return hours || null;
    }

    nextClosure(closures: CalendarEvent[]): dayjs.Dayjs | null {
        const hours = this.openHoursDefinition();
        const hoursCloseDate = hours?.scheduleEnd ? dayjs(hours.scheduleEnd) : null;

        const nextClosure = closures
            .filter((closure) => dayjs(closure.startAt).isAfter(dayjs()))
            .sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)))[0];

        const nextClosureDate = nextClosure ? dayjs(nextClosure.startAt) : null;

        if (hoursCloseDate && nextClosureDate) {
            return hoursCloseDate.isBefore(nextClosureDate) ? hoursCloseDate : nextClosureDate;
        }

        return hoursCloseDate || nextClosureDate || null;
    }

    openDate(closures: CalendarEvent[]): string {
        const hours = this.openHoursDefinition();
        const scheduleStart = hours?.scheduleStart ? dayjs(hours.scheduleStart) : null;
        const scheduleEnd = hours?.scheduleEnd ? dayjs(hours.scheduleEnd) : null;

        const closuresHappeningNow = closures.filter(
            (closure) => dayjs(closure.startAt).isBefore(dayjs()) && dayjs(closure.endAt).isAfter(dayjs())
        );
        const soonestClosureEnd = closuresHappeningNow
            .map((closure) => dayjs(closure.endAt))
            .sort((a, b) => a.diff(b))[0];

        if (scheduleEnd?.isBefore(dayjs())) {
            return "Closed Until Further Notice";
        }

        if (scheduleStart && soonestClosureEnd) {
            return (
                "Opens: " +
                (scheduleStart.isAfter(soonestClosureEnd) ? scheduleStart : soonestClosureEnd).format("MM/DD/YYYY")
            );
        }

        if (soonestClosureEnd) {
            return "Opens: " + soonestClosureEnd.format("MM/DD/YYYY");
        }

        if (scheduleStart) {
            return "Opens: " + scheduleStart.format("MM/DD/YYYY");
        }

        return "";
    }
    name = this.equipmentName;
}
